import React, { useCallback } from 'react'
import { Button, Dropdown, MenuProps } from 'antd'
import { MoreOutlined, FileExcelOutlined } from '@ant-design/icons'
import { GridApi, ExcelExportParams } from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import sanitizeFilename from '../../../utils/sanitizeFilename'

interface ActionsMenuProps<T = any> {
  /**
   * AG Grid API instance for export functionality
   */
  gridApi?: GridApi<T>

  /**
   * Default filename for exports (without extension)
   */
  fileName?: string

  /**
   * Excel export options (used only if onExportExcel is not provided)
   */
  excelExportOptions?: Partial<ExcelExportParams>

  /**
   * Custom Excel export handler
   * If provided, this will be used instead of the default export behavior
   */
  onExportExcel?: () => void

  /**
   * Additional custom menu items to add to the dropdown
   */
  customMenuItems?: MenuProps['items']

  /**
   * Callback when a menu item is clicked
   */
  onMenuClick?: (key: string) => void

  /**
   * Show export options (Excel)
   */
  showExport?: boolean
}

/**
 * Actions menu component for GridToolbar
 * Displays a three-dot menu with export and custom actions
 *
 * @example
 * ```tsx
 * // Simple export menu
 * <GridToolbar>
 *   <GridToolbar.Actions>
 *     <GridToolbar.ActionsMenu
 *       gridApi={gridApi}
 *       fileName="my-data"
 *     />
 *   </GridToolbar.Actions>
 * </GridToolbar>
 *
 * // With custom actions
 * <GridToolbar>
 *   <GridToolbar.Actions>
 *     <GridToolbar.ActionsMenu
 *       gridApi={gridApi}
 *       fileName="my-data"
 *       customMenuItems={[
 *         { key: 'print', label: 'Print', icon: <PrinterOutlined /> },
 *         { type: 'divider' },
 *         { key: 'settings', label: 'Settings', icon: <SettingOutlined /> }
 *       ]}
 *       onMenuClick={(key) => {
 *         if (key === 'print') handlePrint()
 *         if (key === 'settings') openSettings()
 *       }}
 *     />
 *   </GridToolbar.Actions>
 * </GridToolbar>
 * ```
 */
export const ActionsMenu = ({
  gridApi,
  fileName = 'export',
  excelExportOptions = {},
  onExportExcel,
  customMenuItems = [],
  onMenuClick,
  showExport = true
}: ActionsMenuProps) => {
  const { t } = useTranslation()

  const handleExportExcel = useCallback(() => {
    // Use custom export handler if provided
    if (onExportExcel) {
      onExportExcel()
      return
    }

    // Default export behavior
    if (!gridApi) return

    const sanitizedFileName = sanitizeFilename(fileName, 'export', 'xlsx')
    gridApi.exportDataAsExcel({
      fileName: sanitizedFileName,
      ...excelExportOptions
    })
  }, [gridApi, fileName, excelExportOptions, onExportExcel])

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'export-excel':
        handleExportExcel()
        break
      default:
        onMenuClick?.(key)
        break
    }
  }

  // Build menu items
  const menuItems: MenuProps['items'] = []

  // Add export options if enabled
  if (showExport && gridApi) {
    menuItems.push({
      key: 'export-excel',
      label: t('global:exportToExcel', 'Export to Excel'),
      icon: <FileExcelOutlined />
    })
  }

  // Add divider if we have both export and custom items
  if (menuItems.length > 0 && customMenuItems && customMenuItems.length > 0) {
    menuItems.push({ type: 'divider' })
  }

  // Add custom menu items
  if (customMenuItems && customMenuItems.length > 0) {
    menuItems.push(...customMenuItems)
  }

  // Don't render if no menu items
  if (menuItems.length === 0) {
    return null
  }

  return (
    <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']} placement="bottomRight">
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  )
}

export default ActionsMenu
